import { HEADER_HEIGHT, MOBILE_SUBHEADER_HEIGHT } from 'components/navigation/header.constants';
import { Section } from 'components/navigation/subheader/SectionsNavigation';
import ScrollableSectionsNavigation from 'components/scrollable-sections-navigation';
import { useScreenBreakpoint } from 'consts/breakpoints';
import { useLocale } from 'locale';
import React from 'react';
import { NavSeparator } from 'screens/DeveloperPage/components/navigation/styled';
import * as Styled from './styled';
import withEvents from 'analytics/withEvents';
import { PageDetails } from 'screens/AgentOfficePages/types';
import { noop } from 'lodash';
import { useRoute } from 'react-router5';
import { Route } from 'config/routes';
import { getAgentOfficeCategory } from 'screens/AgentOfficePages/helper';


interface NavigationParams {
  sections: Section[];
  activeSection: Section;
  isSticky?: boolean;
  onMenuClickEvent?: (event: string, category: string, source: string, eventSource: string, pageDetails: PageDetails) => void;
  pageDetails?: PageDetails;
}

const COLLAPSED_SUBHEADER_HEIGHT = 107;
const FULL_SUBHEADER_HEIGHT = 262;
const FULL_SUBHEADER_MOBILE_HEIGHT = 162;

const mapNavMenuToEvent = {
  [Section.OVERVIEW]: 'overview',
  [Section.ABOUT]: 'about',
  [Section.TEAM]: 'team',
  [Section.LISTINGS]: 'active_listings',
  [Section.DEALS]: 'deals_history',
  [Section.REVIEWS]: 'reviews',
};

const NavigationBase: React.FC<NavigationParams> = ({ sections, activeSection, isSticky, pageDetails, onMenuClickEvent = noop }) => {
  const { t } = useLocale();
  const isMobile = useScreenBreakpoint() === 1;
  const { route } = useRoute();
  const isOfficePage = route.name === Route.OfficePage;
  const event = `${getAgentOfficeCategory(isOfficePage)}_top_navigation_bar_click`;
  const category = getAgentOfficeCategory(isOfficePage);
  const eventSource = route.params.source || 'direct';

  const eventHandler = (item: Section) => {
    onMenuClickEvent(event, category, mapNavMenuToEvent[item], eventSource, pageDetails);
  };

  const items = sections.map(item => ({ label: t('officePage.subMenu.item', { item }), value: item }));
  const topOffset = isMobile ? MOBILE_SUBHEADER_HEIGHT * 2 : HEADER_HEIGHT + COLLAPSED_SUBHEADER_HEIGHT;

  const topOffsetMap = {
    [sections[0]]: isMobile ? MOBILE_SUBHEADER_HEIGHT + FULL_SUBHEADER_MOBILE_HEIGHT : HEADER_HEIGHT + FULL_SUBHEADER_HEIGHT,
    ...(sections.slice(1).reduce((res, section) => ({
      ...res,
      [section]: isMobile ? MOBILE_SUBHEADER_HEIGHT * 2 : HEADER_HEIGHT + COLLAPSED_SUBHEADER_HEIGHT,
    }), {})),
  };

  const Wrapper = isMobile ? React.Fragment : Styled.InnerWrapper;
  const SectionWrapper = isMobile ? Styled.MobileNavWrapper : Styled.NavigationWrapper;

  return (
    <SectionWrapper isSticky={isSticky}>
      <Wrapper>
        <ScrollableSectionsNavigation
          separator={<NavSeparator />}
          items={items}
          activeItem={activeSection}
          topOffset={topOffset}
          topOffsetMap={topOffsetMap}
          scrollOnClick={sections.length > 1}
          onItemClickNotify={eventHandler}
        />
      </Wrapper>
    </SectionWrapper>
  );
};

export const Navigation = withEvents<NavigationParams>((sendEvent) => ({
  onMenuClickEvent: (event: string, category: string, source: string, eventSource: string, pageDetails: PageDetails) => {
    sendEvent(event, category, {
      ...pageDetails,
      event: {
        page_section: source,
        source: eventSource,
      },
    });
  },
}))(NavigationBase);
